import { useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { http, euro, STATUS_LABEL, loginWithGoogle } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MarginGuide } from "@/components/site/MarginGuide";

const Kpi = ({ label, value, id, accent }) => (
  <div data-testid={`kpi-${id}`} className={`rounded-2xl border border-[var(--line)] p-5 ${accent ? "bg-[var(--ink)] text-[var(--bone)]" : "bg-white"}`}>
    <p className={`text-xs ${accent ? "text-[#BDB5A8]" : "text-[var(--muted)]"}`}>{label}</p>
    <p className="font-display mt-2 text-3xl">{value}</p>
  </div>
);

const OrderRow = ({ o, onStatus }) => (
  <tr data-testid={`admin-order-${o.id}`} className="border-b border-[var(--line)] align-top">
    <td className="py-4 pr-4">
      <p className="font-medium">N° {o.id.slice(-8).toUpperCase()}</p>
      <p className="text-xs text-[var(--muted)]">{new Date(o.created_at).toLocaleString("fr-FR")}</p>
    </td>
    <td className="py-4 pr-4">
      <p>{o.name}</p><p className="text-xs text-[var(--muted)]">{o.email}</p>
      {o.address && <p className="mt-1 max-w-[220px] text-xs text-[var(--muted)]">{o.address}</p>}
    </td>
    <td className="py-4 pr-4 text-xs">{(o.lines ?? []).map((l) => <p key={l.product_id}>{l.qty} × {l.name}</p>)}</td>
    <td className="py-4 pr-4 whitespace-nowrap">{euro(o.total)}</td>
    <td className="py-4 pr-4 whitespace-nowrap text-[#4A6136] font-medium">{euro(o.margin_ht)}<p className="text-xs font-normal text-[var(--muted)]">TVA {euro(o.vat)}</p></td>
    <td className="py-4">
      <select data-testid={`admin-status-${o.id}`} value={o.status} onChange={(e) => onStatus(o.id, e.target.value)}
        className="rounded-full border border-[var(--line)] bg-white px-3 py-1.5 text-xs">
        {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
    </td>
  </tr>
);

export default function Admin() {
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);

  const load = () => http.get("/admin/stats").then((r) => setData(r.data)).catch(() => setData(false));
  useEffect(() => {
    if (user?.is_admin) load();
  }, [user]);

  const onStatus = async (id, status) => {
    try {
      await http.patch(`/admin/orders/${id}`, { status });
      toast.success(`Statut : ${STATUS_LABEL[status]}`);
      load();
    } catch {
      toast.error("Mise à jour impossible");
    }
  };

  if (loading) return <div className="min-h-screen" />;
  if (!user || !user.is_admin)
    return (
      <div data-testid="admin-access-denied" className="mx-auto max-w-xl px-5 pt-40 pb-20 text-center">
        <h1 className="font-display text-4xl font-light">Espace propriétaire</h1>
        <p className="mt-4 text-[var(--muted)]">{user ? "Ce compte n'a pas accès au tableau de bord." : "Connectez-vous avec le compte Google du propriétaire."}</p>
        {!user && <button data-testid="admin-login-button" onClick={loginWithGoogle} className="mt-8 rounded-full bg-[var(--ink)] px-8 py-4 text-sm text-[var(--bone)]">Continuer avec Google</button>}
      </div>
    );
  if (!data) return <div className="min-h-screen pt-40 text-center text-[var(--muted)]">{data === false ? "Erreur de chargement." : "Chargement…"}</div>;
  const k = data.kpis ?? {};
  const orders = Array.isArray(data.orders) ? data.orders : [];
  const products = Array.isArray(data.products) ? data.products : [];

  return (
    <div data-testid="admin-page" className="mx-auto max-w-[1440px] px-5 pt-[110px] pb-10 sm:px-10">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="font-mono-label text-[11px] text-[var(--muted)]">Tableau de bord propriétaire</p>
          <h1 className="font-display mt-3 text-4xl sm:text-5xl font-light">Vos <em className="italic text-[var(--terra)]">chiffres</em></h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <a href="https://dashboard.stripe.com/payments" target="_blank" rel="noopener noreferrer" data-testid="admin-stripe-link"
            className="inline-flex items-center gap-2 rounded-full bg-[#635BFF] px-5 py-3 text-sm font-semibold text-white">
            Paiements carte (Stripe) <ExternalLink className="h-4 w-4" />
          </a>
          <a href="https://www.paypal.com/myaccount/activities" target="_blank" rel="noopener noreferrer" data-testid="admin-paypal-link"
            className="inline-flex items-center gap-2 rounded-full bg-[#FFC439] px-5 py-3 text-sm font-semibold text-[#003087]">
            Paiements PayPal <ExternalLink className="h-4 w-4" />
          </a>
        </div>
      </div>

      <div className="mt-10 grid grid-cols-2 gap-3 lg:grid-cols-7">
        <Kpi id="paid-revenue" label="CA encaissé (payé)" value={euro(k.paid_revenue)} accent />
        <Kpi id="paid-margin" label="Marge nette après TVA" value={euro(k.paid_margin_ht)} />
        <Kpi id="paid-vat" label="TVA à reverser (20 %)" value={euro(k.paid_vat)} />
        <Kpi id="pending" label="CA en attente" value={euro(k.pending_revenue)} />
        <Kpi id="to-verify" label="Paiements à vérifier" value={k.to_verify ?? 0} />
        <Kpi id="to-ship" label="À commander au fournisseur" value={k.to_ship ?? 0} />
        <Kpi id="newsletter" label="Inscrits newsletter" value={k.newsletter ?? 0} />
      </div>

      <section className="mt-14">
        <h2 className="font-display text-2xl">Commandes ({k.orders_count ?? 0})</h2>
        <p className="mt-1 text-sm text-[var(--muted)]">Vérifiez chaque paiement dans PayPal (montant + n° en note), puis passez la commande en « Payée ». Pour les objets, commandez chez le fournisseur puis passez en « Expédiée ».</p>
        <div className="mt-6 overflow-x-auto" data-lenis-prevent>
          <table className="w-full min-w-[860px] text-left text-sm">
            <thead className="text-xs text-[var(--muted)]"><tr className="border-b border-[var(--line)]">
              {["Commande", "Client", "Articles", "Total TTC", "Marge après TVA", "Statut"].map((h) => <th key={h} className="py-3 pr-4 font-normal">{h}</th>)}
            </tr></thead>
            <tbody>
              {orders.length === 0 ? <tr><td colSpan={6} className="py-10 text-center text-[var(--muted)]">Aucune commande pour l'instant.</td></tr>
                : orders.map((o) => <OrderRow key={o.id} o={o} onStatus={onStatus} />)}
            </tbody>
          </table>
        </div>
      </section>

      <MarginGuide />
      <section className="mt-16">
        <h2 className="font-display text-2xl">Vos liens partenaires</h2>
        <p data-testid="admin-amazon-tag-status" className="mt-1 text-sm text-[var(--muted)]">
          {data.amazon_tag ? `Identifiant Amazon Partenaires actif : ${data.amazon_tag}.` : "Aucun identifiant Amazon Partenaires : les liens Amazon ne vous rapportent pas encore de commission."}{" "}
          Vos gains (commissions) sont visibles dans l'espace de chaque programme partenaire.
        </p>
        <div className="mt-6 overflow-x-auto" data-lenis-prevent>
          <table data-testid="admin-partner-links-table" className="w-full min-w-[640px] text-left text-sm">
            <thead className="text-xs text-[var(--muted)]"><tr className="border-b border-[var(--line)]">
              {["Produit", "Partenaire", "Commission", "Gain pour 100 € d'achat", "Lien"].map((h) => <th key={h} className="py-3 pr-4 font-normal">{h}</th>)}
            </tr></thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} className="border-b border-[var(--line)]">
                  <td className="py-3 pr-4">{p.name}</td>
                  <td className="py-3 pr-4 text-[var(--muted)]">{p.partner ?? "—"}</td>
                  <td className="py-3 pr-4">{p.commission ?? "—"}</td>
                  <td className="py-3 pr-4 font-medium text-[#4A6136]">{p.gain_per_100 != null ? euro(p.gain_per_100) : "—"}</td>
                  <td className="py-3 pr-4 max-w-[360px] truncate"><a href={p.affiliate_url} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">{p.affiliate_url}</a></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
